import React from "react";
import { MainContainer } from "../Components/Elements";
import { Button, Offcanvas, Table } from "react-bootstrap";
import NoData from "../Components/NoData";
import Shimmer from "../Components/Shimmer";
import GlobalSettingsHook from "../hooks/GlobalSettingsHook";
import editIcon from "../assets/icons/edit.png";
import MainInput from "../Components/MainInput";
import "../css/GlobalSettings.css";

const Field = ({
  label,
  value,
  onChange,
  type = "text",
  min,
  dir = "ltr",
  placeholder,
}) => (
  <label className="app-policy-field" dir={dir}>
    <span>{label}</span>
    <input
      type={type}
      min={min}
      value={value}
      placeholder={placeholder}
      onChange={(event) => onChange(event.target.value)}
    />
  </label>
);

const TextAreaField = ({ label, value, onChange, dir = "ltr" }) => (
  <label className="app-policy-field app-policy-field-wide" dir={dir}>
    <span>{label}</span>
    <textarea
      rows={4}
      value={value}
      onChange={(event) => onChange(event.target.value)}
    />
  </label>
);

const SummaryItem = ({ label, value }) => (
  <div className="app-policy-summary-item">
    <span>{label}</span>
    <strong>{value || "-"}</strong>
  </div>
);

const GlobalSettings = () => {
  const {
    users,
    loading,
    settingsSaveLoading,
    appUpdateSaveLoading,
    handleUpdate,
    handleAppUpdatePolicySave,
    setShowModal,
    showModal,
    setPlatformFeePercentage,
    setgracePeriod,
    setdelayDayFees,
    gracePeriod,
    platformFeePercentage,
    delayDayFees
    ,handleEditGobalSettings,
    androidPolicy,
    optionalPromptModes,
    updateAndroidPolicy,
    updateAndroidPolicyText
  } = GlobalSettingsHook();

  const platformFeePolicy = users?.platformFeePolicy || {};
  const isSavingAppPolicy = loading || appUpdateSaveLoading;

  return (
    <>
      <MainContainer>
        <div className="border-bottom pb-3">
          <Table hover className="text-center">
            <thead>
              <tr>
                <th className="bg-light">Platform Fees</th>
                <th className="bg-light">Platform Fee Base</th>
                <th className="bg-light">Legacy Grace Period</th>
                <th className="bg-light">Legacy Delay Day Fees</th>
                <th className="bg-light">Legacy User Rate Penalty</th>
                <th className="bg-light">Legacy User Rate Penalty After</th>
                <th className="bg-light">Update</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 3 }, (_, index) => (
                  <Shimmer key={index} type={"table"} colSpan={11} />
                ))
              ) : users.length === 0 ? (
                <NoData />
              ) : (
                <tr>
                  <td>{platformFeePolicy?.feePercentage ?? "-"} %</td>
                  <td>Cash Price</td>
                  <td>{users?.gracePeriod} Day</td>
                  <td>{users?.delayDayFees} EGP</td>
                  <td>{users?.userRatePenalty} <i className="fa-solid fa-star"></i></td>
                  <td>{users?.userRatePenaltyAfter} {users?.userRatePenaltyAfter > 1 ? "Days" : "Day"}</td>
                  <td>
                    {" "}
                    <img
                      src={editIcon}
                      alt="Edit global settings"
                      onClick={() => handleEditGobalSettings(users)}
                      style={{ cursor: "pointer", color: "blue" }}
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
        <section className="app-update-policy-section">
          <div className="app-policy-card">
            <div className="app-policy-header">
              <div>
                <p className="app-policy-kicker">Mobile release control</p>
                <h4>App Update Policy</h4>
              </div>
              <Button
                type="submit"
                form="app-update-policy-form"
                className="app-policy-save"
                disabled={isSavingAppPolicy}
              >
                {appUpdateSaveLoading ? (
                  <i className="fa-solid fa-fan fa-spin fa-spin-reverse"></i>
                ) : (
                  "Save App Update Policy"
                )}
              </Button>
            </div>

            <div className="app-policy-summary-grid">
              <SummaryItem
                label="Latest Android version"
                value={`${androidPolicy.latestVersionName || "-"} (${
                  androidPolicy.latestVersionCode ?? "-"
                })`}
              />
              <SummaryItem
                label="Minimum supported code"
                value={androidPolicy.minimumSupportedVersionCode}
              />
              <SummaryItem
                label="Optional prompts"
                value={
                  androidPolicy.optionalUpdateEnabled ? "Enabled" : "Disabled"
                }
              />
              <SummaryItem
                label="Prompt mode"
                value={androidPolicy.optionalPromptMode}
              />
            </div>

            <form
              id="app-update-policy-form"
              className="app-policy-form"
              onSubmit={handleAppUpdatePolicySave}
            >
              <div className="app-policy-group">
                <div className="app-policy-group-title">
                  <h5>Android version gates</h5>
                </div>
                <div className="app-policy-grid">
                  <Field
                    label="Latest Version Code"
                    type="number"
                    min={0}
                    value={androidPolicy.latestVersionCode}
                    onChange={(value) =>
                      updateAndroidPolicy("latestVersionCode", value)
                    }
                  />
                  <Field
                    label="Latest Version Name"
                    value={androidPolicy.latestVersionName}
                    onChange={(value) =>
                      updateAndroidPolicy("latestVersionName", value)
                    }
                  />
                  <Field
                    label="Minimum Supported Version Code"
                    type="number"
                    min={0}
                    value={androidPolicy.minimumSupportedVersionCode}
                    onChange={(value) =>
                      updateAndroidPolicy(
                        "minimumSupportedVersionCode",
                        value
                      )
                    }
                  />
                  <Field
                    label="Play Store URL"
                    value={androidPolicy.playStoreUrl}
                    onChange={(value) =>
                      updateAndroidPolicy("playStoreUrl", value)
                    }
                    placeholder="https://play.google.com/store/apps/details?id=..."
                  />
                </div>
              </div>

              <div className="app-policy-group">
                <div className="app-policy-group-title">
                  <h5>Optional update prompts</h5>
                </div>
                <div className="app-policy-grid app-policy-grid-compact">
                  <label className="app-policy-toggle">
                    <input
                      type="checkbox"
                      checked={Boolean(androidPolicy.optionalUpdateEnabled)}
                      onChange={(event) =>
                        updateAndroidPolicy(
                          "optionalUpdateEnabled",
                          event.target.checked
                        )
                      }
                    />
                    <span>
                      <strong>Show optional update prompts</strong>
                    </span>
                  </label>
                  <label className="app-policy-field">
                    <span>Optional Prompt Mode</span>
                    <select
                      value={androidPolicy.optionalPromptMode}
                      onChange={(event) =>
                        updateAndroidPolicy(
                          "optionalPromptMode",
                          event.target.value
                        )
                      }
                    >
                      {optionalPromptModes.map((mode) => (
                        <option key={mode} value={mode}>
                          {mode}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </div>

              <div className="app-policy-group">
                <div className="app-policy-group-title">
                  <h5>Required update copy</h5>
                </div>
                <div className="app-policy-grid">
                  <Field
                    label="Title EN"
                    value={androidPolicy.forceUpdateTitle?.en || ""}
                    onChange={(value) =>
                      updateAndroidPolicyText("forceUpdateTitle", "en", value)
                    }
                  />
                  <Field
                    label="Title AR"
                    dir="rtl"
                    value={androidPolicy.forceUpdateTitle?.ar || ""}
                    onChange={(value) =>
                      updateAndroidPolicyText("forceUpdateTitle", "ar", value)
                    }
                  />
                  <TextAreaField
                    label="Message EN"
                    value={androidPolicy.forceUpdateMessage?.en || ""}
                    onChange={(value) =>
                      updateAndroidPolicyText(
                        "forceUpdateMessage",
                        "en",
                        value
                      )
                    }
                  />
                  <TextAreaField
                    label="Message AR"
                    dir="rtl"
                    value={androidPolicy.forceUpdateMessage?.ar || ""}
                    onChange={(value) =>
                      updateAndroidPolicyText(
                        "forceUpdateMessage",
                        "ar",
                        value
                      )
                    }
                  />
                </div>
              </div>

              <div className="app-policy-group">
                <div className="app-policy-group-title">
                  <h5>Optional update copy</h5>
                </div>
                <div className="app-policy-grid">
                  <Field
                    label="Title EN"
                    value={androidPolicy.optionalUpdateTitle?.en || ""}
                    onChange={(value) =>
                      updateAndroidPolicyText(
                        "optionalUpdateTitle",
                        "en",
                        value
                      )
                    }
                  />
                  <Field
                    label="Title AR"
                    dir="rtl"
                    value={androidPolicy.optionalUpdateTitle?.ar || ""}
                    onChange={(value) =>
                      updateAndroidPolicyText(
                        "optionalUpdateTitle",
                        "ar",
                        value
                      )
                    }
                  />
                  <TextAreaField
                    label="Message EN"
                    value={androidPolicy.optionalUpdateMessage?.en || ""}
                    onChange={(value) =>
                      updateAndroidPolicyText(
                        "optionalUpdateMessage",
                        "en",
                        value
                      )
                    }
                  />
                  <TextAreaField
                    label="Message AR"
                    dir="rtl"
                    value={androidPolicy.optionalUpdateMessage?.ar || ""}
                    onChange={(value) =>
                      updateAndroidPolicyText(
                        "optionalUpdateMessage",
                        "ar",
                        value
                      )
                    }
                  />
                </div>
              </div>
            </form>
          </div>
        </section>
        <Offcanvas
          show={showModal}
          onHide={() => setShowModal(false)}
          placement="end"
        >
          <Offcanvas.Header className="pt-5" closeButton>
            <Offcanvas.Title>Edit Global Settings</Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body className="d-flex flex-column justify-content-between">
            <form
              className="d-flex flex-column justify-content-between h-100"
              onSubmit={handleUpdate}
            >
              <div>
                <MainInput
                  label={"Platform Fees %*"}
                  type="number"
                  name="platformFeePercentage"
                  value={platformFeePercentage}
                  onChange={(e) => {
                    setPlatformFeePercentage(e.target.value);
                  }}
                />

                <MainInput
                  label={"Legacy grace Period*"}
                  type="number"
                  name="gracePeriod"
                  value={gracePeriod}
                  onChange={(e) => {
                    setgracePeriod(e.target.value);
                  }}
                />

                <MainInput
                  label={"Legacy Delay Day Fees*"}
                  type="number"
                  name="delayDayFees"
                  value={delayDayFees}
                  onChange={(e) => {
                    setdelayDayFees(e.target.value);
                  }}
                />
              </div>
              <div className="d-flex justify-content-center align-items-center pb-3">
                <button
                  type="submit"
                  disabled={settingsSaveLoading}
                  className="btn bg-white text-main shadow-lg w-75 py-2 fw-bold"
                >
                  {settingsSaveLoading ? (
                    <i className="fa-solid fa-fan fa-spin fa-spin-reverse"></i>
                  ) : (
                    "Submit"
                  )}
                </button>
              </div>
            </form>
          </Offcanvas.Body>
        </Offcanvas>
      </MainContainer>
    </>
  );
};

export default GlobalSettings;
